import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Hero from '../components/Hero';
import Features from '../containers/Features';
import BookingForm from '../components/BookingForm';
import { useLocation } from 'react-router-dom';

const AnimatedSection = ({ children, delay = 0, direction = 'up' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const variants = {
    hidden: {
      y: direction === 'up' ? 50 : direction === 'down' ? -50 : 0,
      x: direction === 'left' ? 50 : direction === 'right' ? -50 : 0,
      opacity: 0,
    },
    visible: {
      y: 0,
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: delay,
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};

const HomePage = () => {
  const location = useLocation();
  
  // For welcome section
  const [welcomeRef, welcomeInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  // For villa image
  const [imageRef, imageInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  // For booking form
  const [formRef, formInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // For call to action section
  const [ctaRef, ctaInView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  useEffect(() => {
    if (location.hash === '#booking-form') {
      const element = document.getElementById('booking-form');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline:'nearest'
          });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div>
      <Hero />
      
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            ref={welcomeRef}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={welcomeInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-blue-900">Welcome to Riverside Bliss Villa</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the serenity of nature at our beautiful riverside homestay in Kallar, Kerala. 
              Our villa offers modern amenities with the tranquility of riverside living.
            </p>
          </motion.div>
          
          <div className="md:flex md:items-center md:gap-12">
            <motion.div 
              ref={imageRef}
              className="md:w-1/2 mb-8 md:mb-0"
              initial={{ opacity: 0, x: -50 }}
              animate={imageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="/homestay/outdoor.jpg" 
                alt="Riverside Bliss Villa" 
                className="rounded-lg shadow-lg w-full h-auto object-cover transform transition-transform duration-500 hover:scale-105" 
              />
            </motion.div>
            <motion.div 
              id='booking-form'
              ref={formRef}
              className="md:w-1/2"
              initial={{ opacity: 0, x: 50 }}
              animate={formInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8 }}
            >
              <BookingForm />
            </motion.div>
          </div>
        </div>
      </section>
      
      <Features />
      
      <motion.section 
        ref={ctaRef}
        className="py-16 bg-blue-800 text-white"
        initial={{ opacity: 0, y: 30 }}
        animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="text-center md:text-left md:w-2/3">
              <h2 className="text-3xl font-bold">Ready for a Riverside Getaway?</h2>
              <p className="mt-4 text-lg opacity-90">
                Book your stay today and experience the beauty of Kallar riverside.
              </p>
            </div>
            <div className="mt-8 md:mt-0 text-center md:text-right">
              <motion.a 
                href="tel:+919400949078"
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold px-6 py-3 rounded-lg text-lg transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Call Us: 9400949078
              </motion.a>
            </div>
          </div>
        </div>
      </motion.section>
          <section className="py-16 bg-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AnimatedSection>
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-blue-900">Frequently Asked Questions</h2>
                    <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                      Find answers to common questions about our riverside villa
                    </p>
                  </div>
                </AnimatedSection>
                
                <div className="mt-12 grid gap-6 max-w-4xl mx-auto">
                  <AnimatedSection delay={0.1}>
                    <motion.div 
                      className="bg-white p-6 rounded-lg shadow-md"
                      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3 className="text-xl font-medium text-blue-900">What are your check-in and check-out times?</h3>
                      <p className="mt-2 text-gray-600">Check-in time is 2:00 PM and check-out time is 11:00 AM. Early check-in or late check-out may be available upon request, subject to availability.</p>
                    </motion.div>
                  </AnimatedSection>
                  
                  <AnimatedSection delay={0.2}>
                    <motion.div 
                      className="bg-white p-6 rounded-lg shadow-md"
                      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3 className="text-xl font-medium text-blue-900">Is there parking available?</h3>
                      <p className="mt-2 text-gray-600">Yes, we provide free parking for all our guests on the premises.</p>
                    </motion.div>
                  </AnimatedSection>
                  
                  <AnimatedSection delay={0.3}>
                    <motion.div 
                      className="bg-white p-6 rounded-lg shadow-md"
                      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3 className="text-xl font-medium text-blue-900">Are pets allowed?</h3>
                      <p className="mt-2 text-gray-600">We're sorry, but pets are not allowed at Riverside Bliss Villa to ensure comfort for all guests.</p>
                    </motion.div>
                  </AnimatedSection>
                  
                  <AnimatedSection delay={0.4}>
                    <motion.div 
                      className="bg-white p-6 rounded-lg shadow-md"
                      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3 className="text-xl font-medium text-blue-900">Do you offer meal services?</h3>
                      <p className="mt-2 text-gray-600">Yes, we offer breakfast and dinner services with advance notice. Please let us know your dietary preferences when booking.</p>
                    </motion.div>
                  </AnimatedSection>
                </div>
              </div>
            </section>
    </div>
  );
};

export default HomePage;